import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, Download } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getDemandaDiario,
  getDemandaTopHoras,
  type DemandaSistema,
  type DemandaTopHoraRow,
} from "@/lib/api";

const STORAGE_KEY = "portal-edocta:demanda-config";
const ANIOS = 6; // 5 años móviles + actual = 6 series
// Líneas de referencia por año: del top-N de picos diarios (orden desc.),
// la #1 (máximo) y la #N (la centésima).
const REF_TOP_N = 100;

const SISTEMAS: DemandaSistema[] = ["SIN", "BCA", "BCS"];

// Mínimo del eje Y por sistema — sube el "piso" para que las diferencias entre
// años se aprecien sin la franja vacía de 0..min.
const Y_MIN: Record<DemandaSistema, number> = {
  SIN: 15000,
  BCA: 900,
  BCS: 200,
};

const MESES = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

// Año más reciente = color más oscuro/saturado.
const COLORS = ["#cbd5e1", "#94a3b8", "#60a5fa", "#3b82f6", "#1d4ed8", "#1e3a8a"];
// Ámbar: complemento operativo (public.demanda_horaria) para los días que aún no
// publica el balance. Se dibuja como serie aparte `${y}__h`.
const HORARIA_COLOR = "#f59e0b";

const LIQ_LABEL: Record<number, string> = {
  0: "preliminar",
  1: "1ª revisión",
  2: "2ª revisión",
  3: "3ª revisión",
  4: "final",
};

const INT_FMT = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });

function fmtMwh(v: string | number | null | undefined): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "—";
  return INT_FMT.format(n);
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

type TablaSortKey = "posicion" | "fecha" | "hora" | "demanda";

// CSV generado en el cliente (los datos del top ya están cargados en memoria).
function topHorasToCsv(rows: DemandaTopHoraRow[]): string {
  const header = ["posicion", "fecha", "hora", "demanda_mw"];
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push([r.posicion, r.fecha, r.hora, r.demanda_mwh ?? ""].join(","));
  }
  // BOM (U+FEFF) para que Excel respete UTF-8; CRLF como fin de línea.
  return String.fromCharCode(0xfeff) + lines.join("\r\n") + "\r\n";
}

function descargarCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

type PivotRow = {
  keyX: string;            // "MM-DD"
  mes: number;             // para tooltip / filtros
  [anio: string]: number | string | undefined;
};

type PivotResult = {
  rows: PivotRow[];
  anios: number[];
  liquidacionPorCelda: Map<string, number>; // key "anio-MM-DD"
  aniosConHoraria: Set<number>;              // años con complemento (serie `${y}__h`)
};

type DiarioRow = {
  anio: number;
  mes: number;
  dia: number;
  pico_mwh: string | null;
  liquidacion_min: number | null;
  fuente: "balance" | "horaria";
};

// CSV de la gráfica (los datos ya están en memoria): una fila por Mes-día y una
// columna por cada año VISIBLE en ese momento. Los valores van redondeados, tal
// como los pinta la gráfica (INT_FMT no muestra decimales), para que ningún
// Excel en español lea el punto decimal como separador de miles.
function graficaToCsv(rows: PivotRow[], anios: number[]): string {
  const header = ["Mes-dia", ...anios.map(String)];
  const lines = [header.join(",")];
  for (const r of rows) {
    const celdas = anios.map((y) => {
      const v = r[String(y)];
      if (typeof v !== "number" || !Number.isFinite(v)) return "";
      return String(Math.round(v));
    });
    lines.push([r.keyX, ...celdas].join(","));
  }
  // BOM (U+FEFF) para que Excel respete UTF-8; CRLF como fin de línea.
  return String.fromCharCode(0xfeff) + lines.join("\r\n") + "\r\n";
}

function pivot(diarias: DiarioRow[]): PivotResult {
  // Base de 366 días usando 2024 (bisiesto) — el slot 02-29 queda vacío para
  // los años no-bisiestos y el eje X queda alineado entre años.
  const rows: PivotRow[] = [];
  const byKey = new Map<string, PivotRow>();
  for (let m = 1; m <= 12; m++) {
    const lastDay = new Date(2024, m, 0).getDate();
    for (let d = 1; d <= lastDay; d++) {
      const k = `${pad(m)}-${pad(d)}`;
      const slot: PivotRow = { keyX: k, mes: m };
      rows.push(slot);
      byKey.set(k, slot);
    }
  }

  const aniosSet = new Set<number>();
  const liq = new Map<string, number>();
  const aniosConHoraria = new Set<number>();
  const maxBalanceKey = new Map<number, string>(); // año -> último MM-DD del balance

  for (const row of diarias) {
    aniosSet.add(row.anio);
    const key = `${pad(row.mes)}-${pad(row.dia)}`;
    const slot = byKey.get(key);
    if (!slot) continue;
    if (row.pico_mwh != null) {
      const n = Number(row.pico_mwh);
      if (Number.isFinite(n)) {
        if (row.fuente === "horaria") {
          // Serie separada `${y}__h` para pintar el complemento en otro color.
          slot[`${row.anio}__h`] = n;
          aniosConHoraria.add(row.anio);
        } else {
          slot[String(row.anio)] = n;
          const prev = maxBalanceKey.get(row.anio);
          if (!prev || key > prev) maxBalanceKey.set(row.anio, key);
        }
      }
    }
    if (row.liquidacion_min != null) {
      liq.set(`${row.anio}-${key}`, row.liquidacion_min);
    }
  }

  // Puente de continuidad: el último día del balance se copia también a la serie
  // horaria, para que las dos líneas (balance y complemento) se toquen sin hueco.
  for (const y of aniosConHoraria) {
    const bkey = maxBalanceKey.get(y);
    if (!bkey) continue;
    const slot = byKey.get(bkey);
    const v = slot?.[String(y)];
    if (slot && typeof v === "number") slot[`${y}__h`] = v;
  }

  return {
    rows,
    anios: [...aniosSet].sort((a, b) => a - b),
    liquidacionPorCelda: liq,
    aniosConHoraria,
  };
}

export function DemandaPage() {
  const stored = useMemo<{ sistema?: DemandaSistema; anios?: number[] }>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const [sistema, setSistema] = useState<DemandaSistema>(stored.sistema ?? "SIN");
  // null = aún no se ha decidido (cargará default = todos los años disponibles)
  const [aniosVisibles, setAniosVisibles] = useState<Set<number> | null>(
    stored.anios ? new Set(stored.anios) : null,
  );
  // Puntos del top-100 de horas sobre la gráfica (por año, mismo color).
  const [mostrarTop, setMostrarTop] = useState(true);
  // Año mostrado en la tabla del top-N (null = aún sin decidir, usa el más reciente)
  const [tablaAnio, setTablaAnio] = useState<number | null>(null);
  const [tablaSortKey, setTablaSortKey] = useState<TablaSortKey>("posicion");
  const [tablaSortDir, setTablaSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          sistema,
          anios: aniosVisibles ? [...aniosVisibles] : undefined,
        }),
      );
    } catch {
      // ignore
    }
  }, [sistema, aniosVisibles]);

  const diarioQ = useQuery({
    queryKey: ["demanda:diario", sistema, ANIOS],
    queryFn: () => getDemandaDiario({ sistema, anios: ANIOS }),
  });

  const topHorasQ = useQuery({
    queryKey: ["demanda:top-horas", sistema, ANIOS, REF_TOP_N],
    queryFn: () => getDemandaTopHoras({ sistema, anios: ANIOS, n: REF_TOP_N }),
  });

  const pivoted = useMemo<PivotResult | null>(() => {
    if (!diarioQ.data) return null;
    return pivot(diarioQ.data.items);
  }, [diarioQ.data]);

  // Inicializar selección al recibir el primer catálogo de años: si no había
  // selección previa, mostrar todos por defecto.
  useEffect(() => {
    if (!pivoted || aniosVisibles !== null) return;
    setAniosVisibles(new Set(pivoted.anios));
  }, [pivoted, aniosVisibles]);

  const colorPorAnio = useMemo(() => {
    const map = new Map<number, string>();
    if (!pivoted) return map;
    const visibles = pivoted.anios.slice(-COLORS.length);
    visibles.forEach((y, i) =>
      map.set(y, COLORS[COLORS.length - visibles.length + i]),
    );
    return map;
  }, [pivoted]);

  // Top-N de horas (TODAS las horas del año, no el pico diario) agrupado por
  // año. Ya viene ordenado por posición ascendente desde el backend.
  const topHorasPorAnio = useMemo(() => {
    const map = new Map<number, DemandaTopHoraRow[]>();
    if (!topHorasQ.data) return map;
    for (const r of topHorasQ.data.items) {
      const arr = map.get(r.anio);
      if (arr) arr.push(r);
      else map.set(r.anio, [r]);
    }
    return map;
  }, [topHorasQ.data]);

  const aniosTop = useMemo(
    () => [...topHorasPorAnio.keys()].sort((a, b) => a - b),
    [topHorasPorAnio],
  );

  // Default de la tabla: el año más reciente con datos.
  useEffect(() => {
    if (tablaAnio !== null || aniosTop.length === 0) return;
    setTablaAnio(aniosTop[aniosTop.length - 1]);
  }, [aniosTop, tablaAnio]);

  // Líneas de referencia por año: #1 (máx = pico anual) y #REF_TOP_N, ambas
  // sobre todas las horas. `rankN` es null si el año no alcanza REF_TOP_N horas.
  const referenciasPorAnio = useMemo(() => {
    const map = new Map<number, { rank1: number; rankN: number | null }>();
    for (const [anio, rows] of topHorasPorAnio) {
      const r1 = rows.find((r) => r.posicion === 1)?.demanda_mwh;
      if (r1 == null) continue;
      const v1 = Number(r1);
      if (!Number.isFinite(v1)) continue;
      const rN = rows.find((r) => r.posicion === REF_TOP_N)?.demanda_mwh;
      const vN = rN != null ? Number(rN) : NaN;
      map.set(anio, { rank1: v1, rankN: Number.isFinite(vN) ? vN : null });
    }
    return map;
  }, [topHorasPorAnio]);

  // Filas de la tabla del año seleccionado, ordenadas según la columna activa.
  const filasTabla = useMemo(() => {
    const base = tablaAnio != null ? topHorasPorAnio.get(tablaAnio) ?? [] : [];
    const rows = [...base];
    rows.sort((a, b) => {
      let cmp = 0;
      if (tablaSortKey === "posicion") cmp = a.posicion - b.posicion;
      else if (tablaSortKey === "hora") cmp = a.hora - b.hora;
      else if (tablaSortKey === "fecha") cmp = a.fecha.localeCompare(b.fecha);
      else {
        const av = a.demanda_mwh != null ? Number(a.demanda_mwh) : -Infinity;
        const bv = b.demanda_mwh != null ? Number(b.demanda_mwh) : -Infinity;
        cmp = av - bv;
      }
      if (cmp === 0) cmp = a.posicion - b.posicion; // orden estable
      return tablaSortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [topHorasPorAnio, tablaAnio, tablaSortKey, tablaSortDir]);

  const handleTablaSort = (k: TablaSortKey) => {
    if (tablaSortKey === k) {
      setTablaSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setTablaSortKey(k);
      setTablaSortDir(k === "demanda" ? "desc" : "asc"); // default intuitivo
    }
  };

  const toggleAnio = (y: number) => {
    const next = new Set(aniosVisibles ?? []);
    if (next.has(y)) next.delete(y);
    else next.add(y);
    setAniosVisibles(next);
  };

  const aniosDibujar = useMemo(() => {
    if (!pivoted) return [] as number[];
    if (!aniosVisibles) return pivoted.anios;
    return pivoted.anios.filter((y) => aniosVisibles.has(y));
  }, [pivoted, aniosVisibles]);

  // Filas de la gráfica + puntos del top-100: por (año, día) se marca la MAYOR
  // demanda de sus horas en el top (varias horas del mismo día = un punto) en la
  // serie `${y}__top`, dibujada solo como puntos (línea invisible).
  const rowsConTop = useMemo(() => {
    if (!pivoted) return [];
    if (!mostrarTop || topHorasPorAnio.size === 0) return pivoted.rows;
    const porDia = new Map<string, number>(); // "anio|MM-DD" → max demanda del top
    for (const [anio, rows] of topHorasPorAnio) {
      for (const r of rows) {
        const v = r.demanda_mwh != null ? Number(r.demanda_mwh) : NaN;
        if (!Number.isFinite(v)) continue;
        const k = `${anio}|${r.fecha.slice(5)}`; // fecha ISO → MM-DD
        const prev = porDia.get(k);
        if (prev == null || v > prev) porDia.set(k, v);
      }
    }
    return pivoted.rows.map((slot) => {
      const extra: Record<string, number> = {};
      for (const y of topHorasPorAnio.keys()) {
        const v = porDia.get(`${y}|${slot.keyX}`);
        if (v != null) extra[`${y}__top`] = v;
      }
      return Object.keys(extra).length ? { ...slot, ...extra } : slot;
    });
  }, [pivoted, topHorasPorAnio, mostrarTop]);

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              Demanda real por sistema
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Pico horario del día. Cada año es una serie; eje X de enero a
              diciembre por día. Fuente:{" "}
              <code className="font-mono">public.demanda_real_balance</code>. Los
              días posteriores al último balance se complementan (en{" "}
              <span className="font-medium" style={{ color: HORARIA_COLOR }}>
                ámbar
              </span>
              ) con <code className="font-mono">public.demanda_horaria</code> y
              también cuentan para las líneas de límite máx/mín.
            </p>
          </div>
          <div className="inline-flex border rounded overflow-hidden">
            {SISTEMAS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSistema(s)}
                className={`px-3 py-1.5 text-sm border-l first:border-l-0 ${
                  sistema === s
                    ? "bg-blue-600 text-white"
                    : "bg-white hover:bg-slate-50 text-slate-700"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        {pivoted && pivoted.anios.length > 0 && (
          <div className="pt-3 mt-3 border-t flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-600 mr-1">Años:</span>
            {pivoted.anios.map((y) => {
              const isOn = aniosVisibles ? aniosVisibles.has(y) : true;
              const color = colorPorAnio.get(y) ?? "#94a3b8";
              return (
                <button
                  key={y}
                  type="button"
                  onClick={() => toggleAnio(y)}
                  className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1 border transition ${
                    isOn
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="font-medium tabular-nums">{y}</span>
                </button>
              );
            })}
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none ml-2">
              <input
                type="checkbox"
                checked={mostrarTop}
                onChange={(e) => setMostrarTop(e.target.checked)}
                className="w-3.5 h-3.5 accent-slate-700 cursor-pointer"
              />
              Puntos top {REF_TOP_N}
            </label>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => setAniosVisibles(new Set(pivoted.anios))}
                className="text-xs border rounded px-2 py-0.5 hover:bg-slate-50"
              >
                Todos
              </button>
              <button
                type="button"
                onClick={() => setAniosVisibles(new Set())}
                className="text-xs border rounded px-2 py-0.5 hover:bg-slate-50"
              >
                Ninguno
              </button>
            </div>
          </div>
        )}
      </div>

      {diarioQ.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          Cargando…
        </div>
      )}
      {diarioQ.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          Error: {(diarioQ.error as Error).message}
        </div>
      )}

      {pivoted && (
        <div className="bg-white border rounded-lg p-4">
          <div className="mb-2 flex items-start justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">
                Pico horario diario (MW)
              </h3>
              <p className="text-[11px] text-slate-500">
                MAX horario del sistema en cada día (suma de áreas por hora,
                luego MAX dentro del día) — eje X Mes-día (29-feb solo aplica a
                años bisiestos). Líneas punteadas por año: demanda #1 (máx, raya
                larga) y #{REF_TOP_N} (punteada) del top-{REF_TOP_N} de picos
                diarios.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                const ys = aniosDibujar;
                const rango =
                  ys.length === 1
                    ? String(ys[0])
                    : `${ys[0]}-${ys[ys.length - 1]}`;
                descargarCsv(
                  `pico_horario_diario_${sistema}_${rango}.csv`,
                  graficaToCsv(pivoted.rows, ys),
                );
              }}
              disabled={aniosDibujar.length === 0 || pivoted.rows.length === 0}
              className="inline-flex items-center gap-1.5 text-xs border rounded px-2.5 py-1 hover:bg-slate-50 disabled:opacity-50 shrink-0"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
          </div>
          <div className="h-[480px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={rowsConTop}
                margin={{ top: 10, right: 24, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="keyX"
                  tick={{ fontSize: 11, fill: "#475569" }}
                  tickFormatter={(v) => {
                    if (typeof v !== "string") return "";
                    const [mm, dd] = v.split("-");
                    return dd === "01" ? MESES[Number(mm) - 1] ?? "" : "";
                  }}
                  interval={0}
                  minTickGap={0}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#475569" }}
                  tickFormatter={(v) => INT_FMT.format(Number(v))}
                  width={72}
                  domain={[
                    Y_MIN[sistema],
                    (dataMax: number) => Math.ceil(dataMax * 1.02),
                  ]}
                  allowDataOverflow
                />
                <Tooltip
                  labelFormatter={(label) => {
                    if (typeof label !== "string") return "";
                    const [mm, dd] = label.split("-");
                    return `${dd} ${MESES[Number(mm) - 1] ?? mm}`;
                  }}
                  formatter={(
                    value: number | string,
                    name: string,
                    payload: { payload?: PivotRow },
                  ) => {
                    const v = fmtMwh(value);
                    const k = payload?.payload?.keyX;
                    const liq = k
                      ? pivoted.liquidacionPorCelda.get(`${name}-${k}`)
                      : undefined;
                    const liqText =
                      liq != null
                        ? ` (${LIQ_LABEL[liq] ?? `liq ${liq}`})`
                        : "";
                    return [`${v}${liqText}`, name];
                  }}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                {aniosDibujar.map((y) => (
                  <Line
                    key={y}
                    type="monotone"
                    dataKey={String(y)}
                    name={String(y)}
                    stroke={colorPorAnio.get(y) ?? "#94a3b8"}
                    strokeWidth={y === pivoted.anios[pivoted.anios.length - 1] ? 2.5 : 1.5}
                    dot={false}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
                {aniosDibujar
                  .filter((y) => pivoted.aniosConHoraria.has(y))
                  .map((y) => (
                    <Line
                      key={`${y}__h`}
                      type="monotone"
                      dataKey={`${y}__h`}
                      name={`${y} · complemento horaria`}
                      stroke={HORARIA_COLOR}
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                {/* Puntos del top-100: solo el marcador, sin línea. */}
                {mostrarTop &&
                  aniosDibujar.map((y) => (
                    <Line
                      key={`${y}__top`}
                      dataKey={`${y}__top`}
                      name={`${y} · top ${REF_TOP_N}`}
                      stroke="none"
                      legendType="circle"
                      dot={{
                        r: 3,
                        fill: colorPorAnio.get(y) ?? "#94a3b8",
                        stroke: "#0f172a",
                        strokeWidth: 0.75,
                      }}
                      activeDot={{ r: 5 }}
                      isAnimationActive={false}
                    />
                  ))}
                {aniosDibujar.flatMap((y) => {
                  const ref = referenciasPorAnio.get(y);
                  if (!ref) return [];
                  const color = colorPorAnio.get(y) ?? "#94a3b8";
                  const lines = [
                    <ReferenceLine
                      key={`ref1-${y}`}
                      y={ref.rank1}
                      stroke={color}
                      strokeWidth={1.5}
                      strokeDasharray="7 4"
                      ifOverflow="hidden"
                      label={{
                        value: `Máx ${y}: ${INT_FMT.format(Math.round(ref.rank1))} MW`,
                        position: "insideBottomRight",
                        fontSize: 11,
                        fill: color,
                      }}
                    />,
                  ];
                  if (ref.rankN != null) {
                    lines.push(
                      <ReferenceLine
                        key={`refN-${y}`}
                        y={ref.rankN}
                        stroke={color}
                        strokeWidth={1.25}
                        strokeDasharray="2 3"
                        ifOverflow="hidden"
                        label={{
                          value: `Min ${y}: ${INT_FMT.format(Math.round(ref.rankN))} MW`,
                          position: "insideTopRight",
                          fontSize: 11,
                          fill: color,
                        }}
                      />,
                    );
                  }
                  return lines;
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {topHorasQ.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          Error cargando el top de horas: {(topHorasQ.error as Error).message}
        </div>
      )}

      {topHorasPorAnio.size > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">
                Top {REF_TOP_N} horas de mayor demanda — {sistema}
              </h3>
              <p className="text-[11px] text-slate-500">
                Todas las horas del año ordenadas de mayor a menor (no el pico
                diario). La #1 y la #{REF_TOP_N} son las líneas de referencia de
                la gráfica.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  if (tablaAnio == null) return;
                  descargarCsv(
                    `top_${REF_TOP_N}_horas_demanda_${sistema}_${tablaAnio}.csv`,
                    topHorasToCsv(filasTabla),
                  );
                }}
                disabled={tablaAnio == null || filasTabla.length === 0}
                className="inline-flex items-center gap-1.5 text-xs border rounded px-2.5 py-1 hover:bg-slate-50 disabled:opacity-50"
              >
                <Download className="w-3.5 h-3.5" />
                CSV
              </button>
              <div className="inline-flex items-center gap-1.5 flex-wrap">
                {aniosTop.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => setTablaAnio(y)}
                    className={`text-xs rounded-full px-3 py-1 border transition ${
                      tablaAnio === y
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="font-medium tabular-nums">{y}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
                <tr>
                  <SortableTh col="posicion" align="right" current={tablaSortKey} dir={tablaSortDir} onSort={handleTablaSort} className="w-12">
                    #
                  </SortableTh>
                  <SortableTh col="fecha" align="left" current={tablaSortKey} dir={tablaSortDir} onSort={handleTablaSort}>
                    Fecha
                  </SortableTh>
                  <SortableTh col="hora" align="right" current={tablaSortKey} dir={tablaSortDir} onSort={handleTablaSort}>
                    Hora
                  </SortableTh>
                  <SortableTh col="demanda" align="right" current={tablaSortKey} dir={tablaSortDir} onSort={handleTablaSort}>
                    Demanda (MW)
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {filasTabla.map((r) => (
                  <tr
                    key={`${r.fecha}-${r.hora}`}
                    className="border-t hover:bg-slate-50"
                  >
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                      {r.posicion}
                    </td>
                    <td className="px-3 py-1.5">
                      {r.fuente === "horaria" && (
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                          style={{ backgroundColor: HORARIA_COLOR }}
                          title="Complemento de demanda_horaria"
                        />
                      )}
                      {r.fecha}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {pad(r.hora)}:00
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                      {fmtMwh(r.demanda_mwh)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SortableTh({
  col,
  align,
  current,
  dir,
  onSort,
  className,
  children,
}: {
  col: TablaSortKey;
  align: "left" | "right";
  current: TablaSortKey;
  dir: "asc" | "desc";
  onSort: (k: TablaSortKey) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const isActive = current === col;
  const Icon = !isActive ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={`px-3 py-2 font-medium select-none cursor-pointer hover:bg-slate-200 ${
        align === "right" ? "text-right" : ""
      } ${className ?? ""}`}
      onClick={() => onSort(col)}
    >
      <span
        className={`inline-flex items-center gap-1 ${
          align === "right" ? "justify-end w-full" : ""
        }`}
      >
        {children}
        <Icon
          className={`w-3 h-3 ${isActive ? "" : "opacity-40"}`}
          aria-hidden="true"
        />
      </span>
    </th>
  );
}
